import { ClassType } from '@frontend/common';
import React from 'react';
import { FormattedMessage } from 'react-intl';

export enum ColliStateName {
    COLLI_CREATED = 'colli_created',
    COLLI_PROCESSING = 'colli_processing',
    COLLI_COMPLETED = 'colli_completed',
    COLLI_COMPLETED_WITH_FAILURES = 'colli_completed_with_failures',
    COLLI_CANCELLED = 'colli_cancelled',
    COLLI_FAILED = 'colli_failed',
    COLLI_DROP_OFF_DONE = 'colli_drop_off_done',
    COLLI_DROP_OFF_PENDING = 'colli_drop_off_pending',
    COLLI_PICK_UP_DONE = 'colli_pick_up_done',
    COLLI_PICK_UP_PENDING = 'colli_pick_up_pending',
    COLLI_IN_TRANSIT = 'colli_in_transit',
    COLLI_BACK_TO_SENDER = 'colli_back_to_sender',
    COLLI_LOST = 'colli_lost'
}

export class ColliState {
    static readonly COLLI_CREATED = new ColliState(
        ColliStateName.COLLI_CREATED,
        (
            <FormattedMessage
                id='ColliState.created'
                description='The displayed value for the ColliState created'
                defaultMessage='Created'
            />
        )
    );
    static readonly COLLI_PROCESSING = new ColliState(
        ColliStateName.COLLI_PROCESSING,
        (
            <FormattedMessage
                id='ColliState.processing'
                description='The displayed value for the ColliState processing'
                defaultMessage='Processing'
            />
        )
    );
    static readonly COLLI_COMPLETED = new ColliState(
        ColliStateName.COLLI_COMPLETED,
        (
            <FormattedMessage
                id='ColliState.completed'
                description='The displayed value for the ColliState completed'
                defaultMessage='Completed'
            />
        )
    );
    static readonly COLLI_COMPLETED_WITH_FAILURES = new ColliState(
        ColliStateName.COLLI_COMPLETED_WITH_FAILURES,
        (
            <FormattedMessage
                id='ColliState.completed_with_failures'
                description='The displayed value for the ColliState completed_with_failures'
                defaultMessage='Completed with failures'
            />
        )
    );
    static readonly COLLI_CANCELLED = new ColliState(
        ColliStateName.COLLI_CANCELLED,
        (
            <FormattedMessage
                id='ColliState.cancelled'
                description='The displayed value for the ColliState cancelled'
                defaultMessage='Cancelled'
            />
        )
    );
    static readonly COLLI_FAILED = new ColliState(
        ColliStateName.COLLI_FAILED,
        (
            <FormattedMessage
                id='ColliState.failed'
                description='The displayed value for the ColliState failed'
                defaultMessage='Failed'
            />
        )
    );
    static readonly COLLI_DROP_OFF_DONE = new ColliState(
        ColliStateName.COLLI_DROP_OFF_DONE,
        (
            <FormattedMessage
                id='ColliState.drop_off_done'
                description='The displayed value for the ColliState drop_off_done'
                defaultMessage='Drop-off done'
            />
        )
    );
    static readonly COLLI_DROP_OFF_PENDING = new ColliState(
        ColliStateName.COLLI_DROP_OFF_PENDING,
        (
            <FormattedMessage
                id='ColliState.drop_off_pending'
                description='The displayed value for the ColliState drop_off_pending'
                defaultMessage='Awaiting drop-off'
            />
        )
    );
    static readonly COLLI_PICK_UP_DONE = new ColliState(
        ColliStateName.COLLI_PICK_UP_DONE,
        (
            <FormattedMessage
                id='ColliState.pick_up_done'
                description='The displayed value for the ColliState pick_up_done'
                defaultMessage='Pick-up done'
            />
        )
    );
    static readonly COLLI_PICK_UP_PENDING = new ColliState(
        ColliStateName.COLLI_PICK_UP_PENDING,
        (
            <FormattedMessage
                id='ColliState.pick_up_pending'
                description='The displayed value for the ColliState pick_up_pending'
                defaultMessage='Awaiting pick-up'
            />
        )
    );
    static readonly COLLI_IN_TRANSIT = new ColliState(
        ColliStateName.COLLI_IN_TRANSIT,
        (
            <FormattedMessage
                id='ColliState.in_transit'
                description='The displayed value for the ColliState in_transit'
                defaultMessage='In transit'
            />
        )
    );
    static readonly COLLI_BACK_TO_SENDER = new ColliState(
        ColliStateName.COLLI_BACK_TO_SENDER,
        (
            <FormattedMessage
                id='ColliState.back_to_sender'
                description='The displayed value for the ColliState back_to_sender'
                defaultMessage='Back to sender'
            />
        )
    );
    static readonly COLLI_LOST = new ColliState(
        ColliStateName.COLLI_LOST,
        (
            <FormattedMessage
                id='ColliState.lost'
                description='The displayed value for the ColliState lost'
                defaultMessage='Lost'
            />
        )
    );

    static readonly ALL = [
        ColliState.COLLI_CREATED,
        ColliState.COLLI_PROCESSING,
        ColliState.COLLI_COMPLETED,
        ColliState.COLLI_COMPLETED_WITH_FAILURES,
        ColliState.COLLI_CANCELLED,
        ColliState.COLLI_FAILED,
        ColliState.COLLI_DROP_OFF_DONE,
        ColliState.COLLI_DROP_OFF_PENDING,
        ColliState.COLLI_PICK_UP_DONE,
        ColliState.COLLI_PICK_UP_PENDING,
        ColliState.COLLI_IN_TRANSIT,
        ColliState.COLLI_BACK_TO_SENDER,
        ColliState.COLLI_LOST
    ];

    private constructor(
        public readonly value: ColliStateName,
        public readonly displayName: React.ReactNode
    ) {}

    static getByValue(state: ColliStateName): ColliState | undefined {
        return this.ALL.find((s) => s.value === state);
    }

    static getType(state: ColliStateName): ClassType {
        switch (state) {
            case ColliStateName.COLLI_COMPLETED:
                return ClassType.SUCCESS;
            case ColliStateName.COLLI_CREATED:
            case ColliStateName.COLLI_PROCESSING:
            case ColliStateName.COLLI_DROP_OFF_DONE:
            case ColliStateName.COLLI_DROP_OFF_PENDING:
            case ColliStateName.COLLI_IN_TRANSIT:
            case ColliStateName.COLLI_LOST:
                return ClassType.WARNING;
            case ColliStateName.COLLI_CANCELLED:
                return ClassType.INFO;
            case ColliStateName.COLLI_COMPLETED_WITH_FAILURES:
            case ColliStateName.COLLI_FAILED:
                return ClassType.DANGER;
            case ColliStateName.COLLI_PICK_UP_DONE:
            case ColliStateName.COLLI_PICK_UP_PENDING:
                return ClassType.SECONDARY;
            default:
                return ClassType.INFO;
        }
    }
}
